import React, { useRef, useEffect, memo } from 'react';

const ACCENT = '#27AAE1';

const ServicesBottomBar = memo(({
  services = [],
  subservices = [],
  currentService,
  currentSubservice,
  language,
  onServiceClick,
  onSubserviceClick,
}) => {
  const servicesScrollRef = useRef(null);
  const subsScrollRef = useRef(null);

  const getSubs = (serviceId) =>
    (subservices || []).filter((s) => {
      const pid = s.parent_service_id ?? s.main_service_id ?? s.service_id ?? s.Subservices;
      return String(pid) === String(serviceId);
    });

  const currentSubs = currentService ? getSubs(currentService.id) : [];

  // Scroll active service/sub into view on mount and when current changes
  useEffect(() => {
    if (!servicesScrollRef.current) return;
    const el = servicesScrollRef.current.querySelector('[data-active-service="true"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentService?.id]);

  useEffect(() => {
    if (!subsScrollRef.current || !currentSubs.length) return;
    const el = subsScrollRef.current.querySelector('[data-active-sub="true"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentSubservice?.id, currentSubs.length]);

  if (!services.length) return null;

  const isRTL = language === 'ar';

  return (
    <div
      className="services-bottom-bar"
      role="navigation"
      aria-label={language === 'ar' ? 'تنقل الخدمات' : 'Services navigation'}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        background: '#fff',
        borderTop: '1px solid #e2e8f0',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        paddingTop: '10px',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
        paddingLeft: 'max(16px, env(safe-area-inset-left))',
        paddingRight: 'max(16px, env(safe-area-inset-right))',
      }}
    >
      {/* صف الخدمات الرئيسية */}
      <div
        ref={servicesScrollRef}
        style={{
          display: 'flex',
          gap: '8px',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x',
          overscrollBehaviorX: 'contain',
          paddingBottom: currentSubs.length > 0 ? '8px' : '0',
          marginBottom: currentSubs.length > 0 ? '4px' : '0',
          borderBottom: currentSubs.length > 0 ? '1px solid #f1f5f9' : 'none',
        }}
        className="services-bottom-bar-services"
      >
        {services.map((service) => {
          const isActive =
            currentService && String(service.id) === String(currentService.id) && !currentSubservice;
          const title =
            language === 'ar'
              ? (service.title_ar || service.title || service.name)
              : (service.title || service.title_en || service.name);
          return (
            <button
              key={service.id}
              type="button"
              data-active-service={isActive}
              onClick={() => onServiceClick && onServiceClick(service.id)}
              style={{
                flexShrink: 0,
                padding: '10px 16px',
                borderRadius: '999px',
                border: isActive ? `2px solid ${ACCENT}` : '1px solid #e2e8f0',
                background: isActive ? 'rgba(39,170,225,0.1)' : '#f8fafc',
                color: isActive ? ACCENT : '#475569',
                fontSize: '13px',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                transition: 'background 0.2s, color 0.2s, border-color 0.2s',
              }}
            >
              {title}
            </button>
          );
        })}
      </div>

      {/* صف الخدمات الفرعية (يظهر فقط للخدمة الحالية) */}
      {currentSubs.length > 0 && (
        <div
          ref={subsScrollRef}
          style={{
            display: 'flex',
            gap: '6px',
            minWidth: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x',
            overscrollBehaviorX: 'contain',
          }}
          className="services-bottom-bar-subs"
        >
          {currentSubs.map((sub) => {
            const isActive = currentSubservice && String(sub.id) === String(currentSubservice.id);
            const subTitle =
              language === 'ar'
                ? (sub.title_ar || sub.title || sub.name)
                : (sub.title || sub.title_en || sub.name);
            return (
              <button
                key={sub.id}
                type="button"
                data-active-sub={isActive}
                onClick={() => onSubserviceClick && onSubserviceClick(sub.id)}
                style={{
                  flexShrink: 0,
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: isActive ? ACCENT : '#f1f5f9',
                  color: isActive ? '#fff' : '#64748b',
                  fontSize: '12px',
                  fontWeight: isActive ? '600' : '500',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                {subTitle}
              </button>
            );
          })}
        </div>
      )}

      <style>{`
        .services-bottom-bar-services,
        .services-bottom-bar-subs {
          -webkit-overflow-scrolling: touch;
        }
        .services-bottom-bar-services::-webkit-scrollbar,
        .services-bottom-bar-subs::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
});

ServicesBottomBar.displayName = 'ServicesBottomBar';

export default ServicesBottomBar;
